import { defaultAuthorizationRequest } from '../../constants/defaultAuthorizationRequest';
import {
  headerButtonUrls,
  loggedInHeaderButtonUrls,
} from '../../features/url/headerUrl';
import { useTheme } from '../../hooks/useTheme';
import { AppStateProvider } from '../../stores/contexts/AppStateContext';
import { LoggedInUser } from '../../types/LoggedInUser';
import { ButtonBase } from '../Button/ButtonBase';
import { ButtonNav } from '../Button/ButtonNav';
import { FiClockIcon, FiHeartIcon, FiMenuIcon } from '../Icon';
import { MenuModal } from '../Modal/MenuModal';

import { HamburgerButton } from './HamburgerButton';
import styles from './Header.module.scss';
import { HeaderIconButton } from './HeaderIconButton';
import { HeaderNavigation } from './HeaderNavigation';
import { HeaderProfileButton } from './HeaderProfileButton';
import { HeaderProps, HeaderButtonGroupProps } from './HeaderProps';

import type { AppEnv } from '@/packages/nurse-senka-frontend-sdk';
import type { FC } from 'react';

// 未ログイン
const HeaderButtonGroup: FC<HeaderButtonGroupProps> = ({
  appEnv,
  authorizationRequest,
  redirectUriAfterRegistration,
  showReadLaterButton,
  showReadingHistoryButton,
}) => {
  const urls = headerButtonUrls(appEnv, authorizationRequest);

  return (
    <div className={styles['header-right']}>
      <div className={styles['header-right-margin']}>
        <ButtonBase
          width="default"
          color="primary"
          link={
            redirectUriAfterRegistration
              ? `${urls.memberRegistration}?redirect_uri=${redirectUriAfterRegistration}`
              : urls.memberRegistration
          }
          customDataAttrGtmClick="header-registration-button"
        >
          新規登録
        </ButtonBase>
      </div>
      <div className={styles['header-right-margin']}>
        <ButtonNav
          color="default"
          link={urls.login}
          customDataAttrGtmClick="header-login-button"
        >
          ログイン
        </ButtonNav>
      </div>
      {showReadLaterButton ? (
        <HeaderIconButton
          link={urls.memberRegistration}
          icon={FiHeartIcon}
          customDataAttrGtmClick="header-read-later-button"
        >
          あとでみる
        </HeaderIconButton>
      ) : (
        ''
      )}
      {showReadingHistoryButton ? (
        <HeaderIconButton
          link={urls.login}
          icon={FiClockIcon}
          customDataAttrGtmClick="header-reading-history-button"
        >
          閲覧履歴
        </HeaderIconButton>
      ) : (
        ''
      )}
    </div>
  );
};

// ログイン済み
type LoggedInHeaderButtonGroupProps = {
  appEnv: AppEnv;
  loggedInUser: LoggedInUser;
  showReadLaterButton?: boolean;
  showReadingHistoryButton?: boolean;
};

const LoggedInHeaderButtonGroup: FC<LoggedInHeaderButtonGroupProps> = ({
  appEnv,
  loggedInUser,
  showReadLaterButton,
  showReadingHistoryButton,
}) => {
  const urls = loggedInHeaderButtonUrls(appEnv);

  return (
    <div className={styles['header-right']}>
      {showReadLaterButton ? (
        <HeaderIconButton
          link=""
          icon={FiHeartIcon}
          customDataAttrGtmClick="header-read-later-button"
        >
          あとでみる
        </HeaderIconButton>
      ) : (
        ''
      )}
      {showReadingHistoryButton ? (
        <HeaderIconButton
          link=""
          icon={FiClockIcon}
          customDataAttrGtmClick="header-reading-history-button"
        >
          閲覧履歴
        </HeaderIconButton>
      ) : (
        ''
      )}
      <div className={styles['only-pc']}>
        <HeaderProfileButton
          appEnv={appEnv}
          icon={FiClockIcon}
          link={urls.my}
          customDataAttrGtmClick="header-my-button"
          loggedInUser={loggedInUser}
        >
          マイページ
        </HeaderProfileButton>
      </div>
    </div>
  );
};

// ヘッダーボディ
export const Header: FC<HeaderProps> = ({
  appEnv,
  authorizationRequest = defaultAuthorizationRequest,
  loggedInUser,
  redirectUriAfterRegistration,
}) => {
  const theme = useTheme();

  return (
    <AppStateProvider>
      <header className={styles.wrap}>
        <div className={styles['header-body']}>
          <HeaderNavigation appEnv={appEnv} theme={theme} />

          {loggedInUser ? (
            <div className={styles['header-login']}>
              <LoggedInHeaderButtonGroup
                appEnv={appEnv}
                loggedInUser={loggedInUser}
              />
              <HamburgerButton icon={FiMenuIcon} />
            </div>
          ) : (
            <div className={styles['header-not-login']}>
              <HeaderButtonGroup
                appEnv={appEnv}
                authorizationRequest={authorizationRequest}
                redirectUriAfterRegistration={redirectUriAfterRegistration}
              />
              <HamburgerButton icon={FiMenuIcon} />
            </div>
          )}
        </div>

        <MenuModal
          theme={theme}
          appEnv={appEnv}
          loggedInUser={loggedInUser}
          authorizationRequest={authorizationRequest}
          redirectUriAfterRegistration={redirectUriAfterRegistration}
        />
      </header>
    </AppStateProvider>
  );
};
